import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import InstanceApi from "../../api/instance/InstanceApi";
import './style.scss'

// Context.
import { InstanceContext } from '../../context/InstanceContext';
import { HostContext } from '../../context/HostContext';

// Components.
import StatusIcon from '../../component/StatusIcon';
import ConditionalRender from "../../helpers/ConditionalRender";
import { hostMetaData, instanceMetaData, instanceTabs } from "../Constants";

// Types
import InstanceTarget from "../../types/instance/InstanceTarget";
import HostTarget from "../../types/host/HostTarget";

// Components
import TagsList from "../TagsList";

function HeaderFilter(props: {
    instanceId: number,
    hostId: number,
    heading: string,
    filterItems?: (item: InstanceTarget | HostTarget) => void,
    type?: string
}) {
    const { instances } = useContext(InstanceContext);
    const { hosts } = useContext(HostContext);
    const [filter, setFilter] = useState<string>('');
    const [metaData, setMetaData] = useState<any>({});
    const searchInputRef = useRef<HTMLInputElement>(null);
    const dropdownMenuRef = useRef<HTMLDivElement>(null);
    // Change state type to Record<string, string>
    const [tags, setTags] = useState<Record<string, string>>({});
    const [isOpen, setIsOpen] = useState(false);
    const metaDataList = props.instanceId ? instanceMetaData : hostMetaData

    const hostsToShow: HostTarget[] = useMemo(() => {
        return JSON.parse(JSON.stringify(hosts))
    }, [hosts])

    const instancesToShow: InstanceTarget[] = useMemo(() => {
        return JSON.parse(JSON.stringify(props.filterItems ? instances.filter(props.filterItems) : instances))
    }, [instances, props.filterItems])

    function clearFilter() {
        // Clear any pre-existing filter values.
        setFilter('');
    }

    useEffect(() => {
        // Reset tags when instanceId changes
        setTags({});
        if (props.instanceId) {
            // Fetch tags and set the Record<string, string> state
            InstanceApi.getTags(props.instanceId).then(fetchedTags => {
                setTags(fetchedTags);
            });
        }
    }, [props.instanceId]);

    useEffect(() => {
        if (props.instanceId) {
            let getSelectedInstanceData = instances.find(instance => instance.id === props.instanceId) || {}
            setMetaData(getSelectedInstanceData)
        }
    }, [props.instanceId]);

    useEffect(() => {
        if (props.hostId) {
            let getSelectedHost = hosts.find(host => host.id === props.hostId) || {}
            setMetaData(getSelectedHost)
        }
    }, [props.hostId]);

    const toggleDropdown = () => {
        setIsOpen((prev) => !prev);
        searchInputRef.current?.focus();
    };

    const handleClickOutside = (event: any) => {
        if (dropdownMenuRef.current && !dropdownMenuRef.current.contains(event.target)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
            <>
                <div className="dropdown d-inline">
                    {(props.instanceId > 0 || props.hostId > 0) && (
                            <StatusIcon instanceId={props.instanceId} hostId={props.hostId} mode="public"/>
                    )}
                    <button type="button" id="filter" className="btn btn-dropdown dropdown-toggle" onClick={toggleDropdown}
                            aria-expanded="false">
                        {((props.heading.length > Number(process.env.REACT_APP_CHART_LEGEND_LENGTH) ? `${props.heading.substring(0, Number(process.env.REACT_APP_CHART_LEGEND_LENGTH))}...` : props.heading))}
                    </button>
                    <div id="dropdown-menu" className={`row g-0 dropdown-menu dropdown-menu-scroll ${isOpen ? "show" : ""}`}
                         aria-labelledby="filter"
                         ref={dropdownMenuRef} onClick={(e) => e.stopPropagation()}>
                        <ul className="col">
                            <li className="search">
                                <div className="row g-0">
                                    <div className="col-9">
                                        <input type="text" autoFocus className="form-control"
                                               placeholder={`Search ${props.type}`} value={filter}
                                               onChange={(e) => setFilter(e.target.value.toLowerCase())}
                                               ref={searchInputRef}/>
                                    </div>
                                    <div className="col-3 text-end">
                                        <button type="button" className="btn btn-small btn-dark" onClick={clearFilter}><i
                                                className="far fa-undo" aria-hidden="true"></i></button>
                                    </div>
                                </div>
                            </li>
                            <li>
                                <hr className="dropdown-divider"/>
                            </li>

                            <ConditionalRender
                                    if={props.type === 'instances' && instancesToShow.length > 0}>
                                {instancesToShow.sort((a: any, b: any) => a.name.localeCompare(b.name)).filter(item => item.name.toLowerCase().includes(filter)).map((item: {
                                    id: number;
                                    name: string;
                                }) => (
                                        <li key={item.id} data-bs-dismiss="dropdown" onClick={() => setIsOpen(false)}>
                                            {item.id === props.instanceId ? (
                                                    <Link to={`/instances/${item.id}/activity`} className="dropdown-item active"
                                                          type="button">{item.name}</Link>
                                            ) : (
                                                    <Link to={`/instances/${item.id}/activity`} className="dropdown-item"
                                                          type="button">{item.name}</Link>
                                            )}
                                        </li>
                                ))}
                            </ConditionalRender>
                            <ConditionalRender if={props.type === 'hosts' && hostsToShow.length > 0}>
                                {hostsToShow.sort((a: any, b: any) => a.name.localeCompare(b.name)).filter(item => item.name.toLowerCase().includes(filter)).map((item: {
                                    id: number;
                                    name: string;
                                }) => (
                                        <li key={item.id} onClick={() => setIsOpen(false)}>
                                            {item.id === props.hostId ? (
                                                    <Link to={`/hosts/${item.id}`} className="dropdown-item active"
                                                          type="button">{item.name}</Link>
                                            ) : (
                                                    <Link to={`/hosts/${item.id}`} className="dropdown-item"
                                                          type="button">{item.name}</Link>
                                            )}
                                        </li>
                                ))}
                            </ConditionalRender>
                        </ul>
                    </div>

                </div>
                <ConditionalRender if={Object.keys(metaData).some((key) => metaDataList.includes(key))}>
                    <div className="dropdown d-inline">

                        <button type="button" id="info" className="btn info-toggle" data-bs-toggle="dropdown"
                                aria-expanded="false">
                            <i className="fa fa-info-circle" aria-hidden="true"/>
                        </button>

                        <div id="info-menu" className="row g-0 dropdown-menu dropdown-menu-scroll" aria-labelledby="info"
                             onClick={(e) => e.stopPropagation()}>
                            {Object.entries(metaData)
                                    .filter(([key]) => metaDataList.includes(key))

                                .map(([key, value]) =>{
                                    const formattedValue = value ? JSON.stringify(value).replaceAll('"', '') : 'N/A';
                                    const isValueTrue = value === true;
                                    const isKeyInInstanceTabs = instanceTabs.includes(key);
                                    const isKeyNotCollected = isValueTrue && isKeyInInstanceTabs && !metaData[`${key.replace('has', 'collect')}`];
                                    return (
                                            <div key={key}>
                                                    <span>{key}</span>
                                                    <span>{formattedValue} {' '}{ isKeyNotCollected ? '(not collected)' : ''}</span>
                                                </div>
                                            );
                            })}
                        </div>
                    </div>
                </ConditionalRender>

                {/* Transform tags back to Tags[] before passing to TagsList */}
                <ConditionalRender if={Object.keys(tags).length > 0}>
                    <TagsList tagList={Object.entries(tags).map(([name, value]) => ({
                        tagname: name,
                        tagvalue: value,
                        datasourceid: props.instanceId // Add datasourceid if needed by Tags type, though TagsList doesn't use it
                    }))} />
                </ConditionalRender>
                <br/>
            </>
    );
}

export default HeaderFilter;
